import React, { useEffect, useState } from "react";
import { Card, Grid, Button, Tab, Tabs } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { projectActions } from "store/project-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchAllProjects,
  fetchAllMilestones,
  fetchAllResources,
  deleteProject,
  deleteMilestone,
  deleteResource,
} from "app/services/project.service";
import { fetchAllClients } from "app/services/client.service";
import { fetchAllMembers } from "app/services/member.service";
import { ProjectDataGrid } from "./ProjectDataGrid";
import { MilestoneDataGrid } from "./MilestoneDataGrid";
import { ResourceDataGrid } from "./ResourceDataGrid";
import AddProjectDialog from "./AddProjectDialog";
import AddMilestoneDialog from "./AddMilestoneDialog";
import AddResourceDialog from "./AddResourceDialog";
import { ConfirmDialog } from "../ConfirmDialog";

export const ProjectAdminView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [editMilestone, setEditMilestone] = useState(null);
  const [editResource, setEditResource] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);

  const projects = useSelector((state) => state.project.projects);
  const milestones = useSelector((state) => state.project.milestones);
  const resources = useSelector((state) => state.project.resources);

  const loadProjects = () => {
    setIsLoading(true);
    fetchAllProjects()
      .then((data) => dispatch(projectActions.setProjects(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  const loadMilestones = () => {
    fetchAllMilestones()
      .then((data) => dispatch(projectActions.setMilestones(data)))
      .catch((error) => handleAxiosError(error));
  };

  const loadResources = () => {
    fetchAllResources()
      .then((data) => dispatch(projectActions.setResources(data)))
      .catch((error) => handleAxiosError(error));
  };

  const loadClients = () => {
    fetchAllClients()
      .then((response) => setClients(response?.data || []))
      .catch((error) => handleAxiosError(error));
  };

  const loadMembers = () => {
    fetchAllMembers()
      .then((response) => setMembers(response?.data || []))
      .catch((error) => handleAxiosError(error));
  };

  useEffect(() => {
    loadProjects();
    loadMilestones();
    loadResources();
    loadClients();
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (item, type) => {
    setDeleteItem(item);
    setDeleteType(type);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteType === "project" && deleteItem) {
      await deleteProject(deleteItem.id);
      loadProjects();
    } else if (deleteType === "milestone" && deleteItem) {
      await deleteMilestone(deleteItem.id);
      loadMilestones();
    } else if (deleteType === "resource" && deleteItem) {
      await deleteResource(deleteItem.id);
      loadResources();
    }
    setDeleteItem(null);
    setDeleteType("");
  };

  // Count by backend ProjectStatus enum values
  const inProgress = projects.filter((p) => p.status === "IN_PROGRESS" || p.status === "PLANNING" || p.status === "APPROVED").length;
  const onHold = projects.filter((p) => p.status === "ON_HOLD" || p.status === "PROPOSED").length;
  const completed = projects.filter((p) => p.status === "COMPLETED").length;

  return (
    <MDBox px={5} py={10}>
      <Grid container spacing={10}>
        {/* Stats Cards */}
        <Grid container spacing={10} item xs={12}>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="info">Total Projects</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{projects.length}</MDTypography>
                <MDTypography variant="caption">All Projects</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="success">In Progress</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{inProgress}</MDTypography>
                <MDTypography variant="caption">Active Projects</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="warning">On Hold</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{onHold}</MDTypography>
                <MDTypography variant="caption">Pending/On Hold</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="info">Completed</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{completed}</MDTypography>
                <MDTypography variant="caption">Finished Projects</MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        {/* Main Content with Tabs */}
        <Grid item xs={12} width="85%">
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Projects" />
                <Tab label="Milestones" />
                <Tab label="Resources" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditProject(null); setProjectDialogOpen(true); }} variant="contained" color="success">
                    Add Project
                  </Button>
                </MDBox>
                <ProjectDataGrid projects={projects} isLoading={isLoading} onEdit={(p) => { setEditProject(p); setProjectDialogOpen(true); }} onDelete={(p) => handleDelete(p, "project")} />
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditMilestone(null); setMilestoneDialogOpen(true); }} variant="contained" color="success">
                    Add Milestone
                  </Button>
                </MDBox>
                <MilestoneDataGrid milestones={milestones} isLoading={isLoading} onEdit={(m) => { setEditMilestone(m); setMilestoneDialogOpen(true); }} onDelete={(m) => handleDelete(m, "milestone")} />
              </MDBox>
            )}
            {tabValue === 2 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditResource(null); setResourceDialogOpen(true); }} variant="contained" color="success">
                    Allocate Resource
                  </Button>
                </MDBox>
                <ResourceDataGrid resources={resources} isLoading={isLoading} onEdit={(r) => { setEditResource(r); setResourceDialogOpen(true); }} onDelete={(r) => handleDelete(r, "resource")} />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>

      <AddProjectDialog open={projectDialogOpen} setOpen={setProjectDialogOpen} onSuccess={loadProjects} editObject={editProject} clients={clients} />
      <AddMilestoneDialog open={milestoneDialogOpen} setOpen={setMilestoneDialogOpen} onSuccess={loadMilestones} editObject={editMilestone} projects={projects} />
      <AddResourceDialog open={resourceDialogOpen} setOpen={setResourceDialogOpen} onSuccess={loadResources} editObject={editResource} projects={projects} members={members} />
      <ConfirmDialog title={`Delete ${deleteType}?`} open={confirmOpen} setOpen={setConfirmOpen} onConfirm={confirmDelete} yesButtonLabel="Delete" noButtonLabel="Cancel">
        Are you sure you want to delete this {deleteType}?
      </ConfirmDialog>
    </MDBox>
  );
};

